import React, { useState, useMemo, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  useTheme,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  LinearProgress,
  Checkbox,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AssignmentIcon from "@mui/icons-material/Assignment";

const TradeBlotterTable = forwardRef(({
  data,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onFilterStateChange,
  onColumnStateChange,
  onSelectionChange,
  viewType = "non-deleted",
  routeList,
}, ref) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [dataGridApiRef, setDataGridApiRef] = useState(null);
  const [filterKey, setFilterKey] = useState(0);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  // Expose DataGrid Pro methods to parent component
  useImperativeHandle(ref, () => ({
    toggleFilterPanel: () => {
      if (dataGridApiRef?.current) {
        // Open DataGrid Pro's built-in filter panel
        dataGridApiRef.current.showFilterPanel();
      }
    },
    clearSelection: () => {
      setRowSelectionModel({
        type: 'include',
        ids: new Set(),
      });
      if (onSelectionChange) {
        onSelectionChange([]);
      }
    }
  }), [dataGridApiRef, onSelectionChange]);

  const handleSelectionChange = (newSelection) => {
    // MUI X Data Grid v8 passes { type: 'include'|'exclude', ids: Set }
    if (newSelection && typeof newSelection === 'object') {

      let idsArray = [];
      const allRowIds = rowsWithExpandInfo.map(row => row.id);
      
      if (newSelection.type === 'exclude') {
        const excludedIds = newSelection.ids instanceof Set
          ? newSelection.ids
          : new Set(Array.isArray(newSelection.ids) ? newSelection.ids : []);
        idsArray = allRowIds.filter(id => !excludedIds.has(id));
      } else {
        idsArray = newSelection.ids instanceof Set
          ? Array.from(newSelection.ids)
          : Array.isArray(newSelection.ids)
            ? newSelection.ids
            : [];
      }
      
      setRowSelectionModel(newSelection);
      
      if (onSelectionChange) {
        onSelectionChange(idsArray);
      }
    }
  };

  const handleFilterModelChange = (model) => {
    // Force re-render of cells when filters change
    setFilterKey(prev => prev + 1);
    
    if (onFilterStateChange) {
      const hasFilters = model.items && model.items.length > 0;
      onFilterStateChange(hasFilters);
    }
  };

  const getSideColor = (side) => {
    switch (side?.toLowerCase()) {
      case "buy":
        return "success";
      case "sell":
        return "error";
      default:
        return "default";
    }
  };

  const getStateColor = (state) => {
    switch (state?.toLowerCase()) {
      case "working":
        return "info";
      case "ready":
        return "success";
      case "pending":
        return "warning";
      default:
        return "default";
    }
  };

  const getOptionLabel = (optionEnum) => {
    // calls=0, puts=1
    if (optionEnum === 0 || optionEnum === "0") return "Calls";
    if (optionEnum === 1 || optionEnum === "1") return "Puts";
    return "";
  };

  const formatExpirationDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  };

  const columns = useMemo(() => [
         {
       field: "rowNumber",
       headerName: "#",
       flex: 0.3,
       minWidth: 40,
       renderCell: (params) => {
         // Use getRowIndexRelativeToVisibleRows for correct sequential numbering with DataGrid Pro filtering
         // The filterKey dependency ensures this re-renders when filters change
         try {
           const relativeIndex = params.api?.getRowIndexRelativeToVisibleRows?.(params.id);
           
           if (relativeIndex !== undefined && relativeIndex >= 0) {
             const rowNumber = relativeIndex + 1;
             return (
               <Typography variant="body2" sx={{ fontWeight: 500 }}>
                 {rowNumber}
               </Typography>
             );
           }
         } catch (error) {
           // Fallback if API method fails
         }
         
         // Fallback to the original value from parent component filtering
         return (
           <Typography variant="body2" sx={{ fontWeight: 500 }}>
             {params.value}
           </Typography>
         );
       },
     },
    {
      field: "timestamp",
      headerName: "Timestamp",
      flex: 0.6,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 400, fontFamily: 'monospace' }}>
          {params.value || "00:00:00"}
        </Typography>
      ),
    },
    {
      field: "distributionType",
      headerName: "Type",
      flex: 0.5,
      minWidth: 80,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Chip
            label={params.value}
            color={params.value === "P" ? "primary" : "default"}
            size="small"
            variant={params.value === "P" ? "filled" : "outlined"}
            sx={{
              fontWeight: 400,
              fontSize: "0.75rem",
              minWidth: "32px",
              height: "24px",
              borderRadius: "0px",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </Box>
      ),
    },
    {
      field: "ticker",
      headerName: "Ticker",
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },

    {
      field: "fund",
      headerName: "Fund",
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "strategy",
      headerName: "Strategy",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
         {
       field: "state",
       headerName: "State",
       flex: 0.7,
       minWidth: 90,
       renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStateColor(params.value)}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
          }}
        />
      ),
    },
    {
      field: "side",
      headerName: "Trade Type",
      flex: 1.2,
      minWidth: 140,
      renderCell: (params) => {
        const isOpeningPosition = params.row.isOpeningPosition;
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              maxHeight: "20px",

              color: isOpeningPosition
                ? theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32'
                : theme.palette.mode === 'dark' ? '#e57373' : '#c62828',
              border: `1px solid ${isOpeningPosition
                ? theme.palette.mode === 'dark' ? '#66bb6a' : '#c8e6c9'
                : theme.palette.mode === 'dark' ? '#ef5350' : '#ffcdd2'}`,
            }}
          />
        );
      },
    },
    {
      field: "quantity",
      headerName: "Quantity",
      flex: 1,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
          {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: "orderType",
      headerName: "Order Type",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "pricePerShare",
      headerName: "Price / Share",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: "monospace" }}>
          ${params.value?.toFixed(2)}
        </Typography>
      ),
    },
    {
      field: "commPerShare",
      headerName: "Comm / Share",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: "monospace" }}>
          ${params.value}
        </Typography>
      ),
    },
    {
      field: "custodian",
      headerName: "Custodian",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "broker",
      headerName: "Broker",
      flex: 1.2,
      minWidth: 80,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "routing",
      headerName: "Routing",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const isAuto = params.value === 0 || params.value === "0";
        return (
          <Chip
            label={isAuto ? "Auto" : "Manual"}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              maxHeight: "20px",
            
              color: isAuto
                ? theme.palette.mode === 'dark' ? '#90caf9' : '#1565c0'
                : theme.palette.mode === 'dark' ? '#ffb74d' : '#e65100',
              border: `1px solid ${isAuto 
                ? theme.palette.mode === 'dark' ? '#42a5f5' : '#bbdefb'
                : theme.palette.mode === 'dark' ? '#ff9800' : '#ffe0b2'}`,
            }}
          />
        );
      },
    },
    {
      field: "routable",
      headerName: "Routable",
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => {
        const isRoutable = params.value === "Yes";
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              maxHeight: "20px",
            
              color: isRoutable
                ? theme.palette.mode === 'dark' ? '#81c784' : '#2e7d32'
                : theme.palette.mode === 'dark' ? '#e57373' : '#c62828',
              border: `1px solid ${isRoutable 
                ? theme.palette.mode === 'dark' ? '#66bb6a' : '#c8e6c9'
                : theme.palette.mode === 'dark' ? '#ef5350' : '#ffcdd2'}`,
            }}
          />
        );
      },
    },
    {
      field: "routeStatus",
      headerName: "Status",
      flex: 0.55,
      minWidth: 108,
      maxWidth: 158,
      renderCell: (params) => {
        const cs = params.row?.cancellationStatus;
        if (cs === 0) {
          return (
            <Chip
              label="Cancel"
              size="small"
              color="error"
              variant="filled"
              sx={{
                fontWeight: 600,
                fontSize: "0.7rem",
                maxHeight: "22px",
                maxWidth: "100%",
                "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          );
        }
        if (cs === 2) {
          return (
            <Chip
              label="Pending"
              size="small"
              color="warning"
              variant="filled"
              sx={{
                fontWeight: 600,
                fontSize: "0.7rem",
                maxHeight: "22px",
                maxWidth: "100%",
                "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          );
        }

        const v = params.value || "Not Routed";
        const color =
          v === "Filled"
            ? "success"
            : v === "Partially Filled"
              ? "warning"
              : v === "Routed"
                ? "info"
                : "default";
        return (
          <Chip
            label={v}
            size="small"
            color={color}
            variant={v === "Not Routed" ? "outlined" : "filled"}
            sx={{
              fontWeight: 600,
              fontSize: "0.7rem",
              maxHeight: "22px",
              maxWidth: "100%",
              "& .MuiChip-label": { px: 0.75, overflow: "hidden", textOverflow: "ellipsis" },
            }}
          />
        );
      },
    },
    {
      field: "routerDisplay",
      headerName: "Router",
      flex: 0.9,
      minWidth: 90,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value || ""}
        </Typography>
      ),
    },
    {
      field: "allocation",
      headerName: "Allocation",
      flex: 0.9,
      minWidth: 120,
      renderCell: (params) => {
        const hasAllocation =
          params?.row?.allocation &&
          typeof params.row.allocation === "object" &&
          Object.keys(params.row.allocation).length > 0;
        return hasAllocation ? (
          <Chip
            label="Allocated"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
          />
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {""}
          </Typography>
        );
      },
    },
    {
      field: "filledPercent",
      headerName: "Filled %",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const filledShares = params.row.filledShares || 0;
        const totalShares = params.row.quantity || 0;
        const tooltipText = `${filledShares.toLocaleString()} / ${totalShares.toLocaleString()} shares filled`;

        return (
          <Tooltip title={tooltipText} arrow placement="top">
            <Box
              sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "help",
              }}
            >
              <LinearProgress
                variant="determinate"
                value={params.value || 0}
                sx={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: 0,
                  backgroundColor: theme.palette.grey[200],
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 0,
                    backgroundColor: params.value >= 100 ? theme.palette.success.main : "rgb(16 168 251 / 64%)",
                  },
                  "& .MuiLinearProgress-root": {
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200],
                  },
                  "& .MuiDataGrid-root": {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                  },
                  "& .MuiDataGrid-columnHeader": {
                    borderRight: `1px solid ${theme.palette.divider}`,
                    color: theme.palette.text.primary,
                  },
                  "& .MuiDataGrid-row": {
                    backgroundColor: theme.palette.background.paper,
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                    "&.Mui-selected": {
                      backgroundColor: theme.palette.action.selected,
                    },
                  },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontFamily: "monospace",
                  textAlign: "center",
                  position: "relative",
                  zIndex: 1,
                  color: params.value >= 100 ? "white" : theme.palette.text.primary,
                }}
              >
                {params.value?.toFixed(2)}%
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "options",
      headerName: "Options",
      flex: 0.32,
      minWidth: 72,
      maxWidth: 96,
      renderCell: (params) => {
        const label = params?.row?.options;
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              fontSize: "0.8125rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {label || ""}
          </Typography>
        );
      },
    },
    {
      field: "strikePrice",
      headerName: "Strike Price",
      flex: 0.44,
      minWidth: 96,
      maxWidth: 128,
      renderCell: (params) => {
        const v = params?.row?.strikePrice;
        const display =
          v === undefined || v === null || v === "" ? "" : Number(v).toLocaleString();
        return (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontFamily: "monospace",
              fontSize: "0.8125rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            {display}
          </Typography>
        );
      },
    },
    {
      field: "expirationDate",
      headerName: "Expiration Date",
      flex: 0.9,
      minWidth: 140,
      renderCell: (params) => {
        const display = params?.row?.expirationDate || "";
        return (
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, fontFamily: "monospace" }}
          >
            {display || ""}
          </Typography>
        );
      },
    },
    // {
    //   field: "router",
    //   headerName: "Router",
    //   flex: 0.8,
    //   minWidth: 50,
    //   sortable: false,
    //   renderCell: (params) => {
    //     const routerId = params.row.router;
    //     const router = routerId ? routeList.find(route => route.routerId === routerId) : null;
    //     const routerAbbreviation = router ? router.abbreviation : "";
    //     return (
    //       <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
    //         {routerAbbreviation || ""}
    //       </Typography>
    //     );
    //   },
    // },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0,
      width: 110,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title={viewType === "deleted" ? "Cannot manage fills for deleted trades" : "Manage Fills"}>
            <span>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => viewType !== "deleted" && params.row.onManageFillsClick?.(params.row)}
                disabled={viewType === "deleted"}
                sx={{
                  "&:hover": {
                    backgroundColor: viewType === "deleted" ? "transparent" : "rgba(156, 39, 176, 0.08)"
                  },
                  opacity: viewType === "deleted" ? 0.5 : 1,
                  cursor: viewType === "deleted" ? "not-allowed" : "pointer"
                }}
              >
                <AssignmentIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={
            viewType === "deleted" ? "Cannot edit deleted trades" :
            params.row.hasProcess ? "Cannot edit - Rebalancer Order" : "Edit Order"
          }>
            <span>
              <IconButton
                size="small"
                color={viewType === "deleted" || params.row.hasProcess ? "disabled" : "primary"}
                onClick={() => viewType !== "deleted" && !params.row.hasProcess && params.row.onEditClick?.(params.row)}
                disabled={viewType === "deleted" || params.row.hasProcess}
                sx={{
                  opacity: (viewType === "deleted" || params.row.hasProcess) ? 0.5 : 1,
                  cursor: (viewType === "deleted" || params.row.hasProcess) ? "not-allowed" : "pointer"
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ], [filterKey, theme]); // Re-create columns when filterKey or theme changes

  const rowsWithExpandInfo = useMemo(() => {
    return data.map((row, index) => ({

      ...row,
      hasDetailPanel: row.distributionType === "P", // Only Parent Trades are expandable
      rowNumber: index + 1, // Add a computed row number field
    }));
  }, [data]);

  const getDetailPanelContent = ({ row }) => {
    if (row.distributionType !== "P") return null; // Only Parent Trades can show detail panel

    return (
      <Box sx={{
        p: 2,
        bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
        borderTop: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
        margin: 0,
      }}>

        {/* Child Trades Section - Only show for Parent Trades - MOVED TO TOP */}
        {row.distributionType === "P" && row.childTrades && row.childTrades.length > 0 && (
          <Box sx={{
            width: "100%",
            pb: 2,
          }}>
            <Typography variant="subtitle2" sx={{
              mb: 2,
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}>
              Child Trades ({row.childTrades.length})
            </Typography>
            <Box sx={{
              overflow: "auto",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[50],
            }}>
              <Box sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr 1fr 1fr 1fr",
                gap: 1,
                p: 1,
                minWidth: "fit-content",
              }}>
                {/* Header Row */}
                <Box sx={{
                  p: 1,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[100],
                }}>#</Box>
                <Box sx={{
                  p: 1,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[100],
                }}>Account</Box>
                <Box sx={{
                  p: 1,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[100],
                }}>Fund</Box>
                <Box sx={{
                  p: 1,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[100],
                }}>Strategy</Box>
                <Box sx={{
                  p: 1,
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  fontSize: "0.75rem",
                  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.grey[100],
                }}>Quantity</Box>

                {/* Data Rows */}
                {row.childTrades.map((childTrade, index) => (
                  <React.Fragment key={index}>
                    <Box sx={{
                      p: 1,
                      fontSize: "0.75rem",
                      borderBottom: index < row.childTrades.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}>
                      {index + 1}
                    </Box>
                    <Box sx={{
                      p: 1,
                      fontSize: "0.75rem",
                      borderBottom: index < row.childTrades.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}>
                      {childTrade.account}
                    </Box>
                    <Box sx={{
                      p: 1,
                      fontSize: "0.75rem",
                      borderBottom: index < row.childTrades.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}>
                      {childTrade.fund}
                    </Box>
                    <Box sx={{
                      p: 1,
                      fontSize: "0.75rem",
                      borderBottom: index < row.childTrades.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}>
                      {childTrade.strategy}
                    </Box>
                    <Box sx={{
                      p: 1,
                      fontSize: "0.75rem",
                      borderBottom: index < row.childTrades.length - 1 ? `1px solid ${theme.palette.divider}` : "none",
                      display: "flex",
                      alignItems: "center",
                      fontFamily: "monospace",
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    }}>
                      {childTrade.sharesOrdered?.toLocaleString()}
                    </Box>

                  </React.Fragment>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* <Box sx={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(3, 1fr)", 
          gap: 2,
          flex: 1,
        }}>
          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}>
              Order Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Ticker:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.ticker}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Trade Type:</Typography>
                <Box
                  sx={{
                    padding: "4px 8px",
                    backgroundColor: row.isOpeningPosition 
                      ? theme.palette.success.main 
                      : theme.palette.error.main,
                    color: "white",
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: "0.7rem",
                    textTransform: "uppercase",
                  }}
                >
                  {row.side}
                </Box>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Action:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.action || "N/A"}</Typography>
              </Box>
                             <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                 <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Position:</Typography>
                 <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.position || "N/A"}</Typography>
               </Box>
               <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                 <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Trade Type:</Typography>
                 <Chip
                   label={row.distributionType === "P" ? "Parent Trade" : "Single Trade"}
                   color={row.distributionType === "P" ? "primary" : "default"}
                   size="small"
                   variant={row.distributionType === "P" ? "filled" : "outlined"}
                   sx={{ 
                     fontWeight: 400, 
                     fontSize: "0.7rem",
                     height: "20px",
                   }}
                 />
               </Box>
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ 
              mb: 1, 
              fontWeight: 600,
              color: theme.palette.text.secondary,
            }}>
              Execution Details
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Quantity:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.quantity?.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Order Type:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.orderType}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Filled %:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.filledPercent?.toFixed(2)}%</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Total Ordered:</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.totalOrderedShares?.toLocaleString() || "N/A"}</Typography>
              </Box>
            </Box>
          </Box>

                     <Box>
             <Typography variant="subtitle2" sx={{ 
               mb: 1, 
               fontWeight: 600,
               color: theme.palette.text.secondary,
             }}>
               Status & Compliance
             </Typography>
             <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
               <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                 <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>State:</Typography>
                 <Chip
                   label={row.state}
                   color={getStateColor(row.state)}
                   size="small"
                   variant="outlined"
                   sx={{ 
                     fontWeight: 600, 
                     fontSize: "0.7rem",
                     borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300],
                     color: theme.palette.text.primary,
                   }}
                 />
               </Box>
               <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                 <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Strategy:</Typography>
                 <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.strategy}</Typography>
               </Box>
               <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                 <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Fund:</Typography>
                 <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>{row.fund}</Typography>
               </Box>
               <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                 <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Order Date:</Typography>
                 <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                   {row.orderDate ? new Date(row.orderDate).toLocaleString() : "N/A"}
                 </Typography>
               </Box>
             </Box>
           </Box>


        </Box> */}
      </Box>
    );
  };

  const getDetailPanelHeight = ({ row }) => {
    return 300;
  };

  return (
    <Box sx={{
    
      width: "100%",
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      overflow: "scroll",
    }}>
      <DataGridPro
        ref={setDataGridApiRef}
        apiRef={dataGridApiRef}
      
     
        rows={rowsWithExpandInfo}
        columns={columns}
        paginationModel={{ page: page, pageSize: pageSize }}
        onPaginationModelChange={(model) => {
          onPageSizeChange(model.pageSize);
          onPageChange(model.page);
        }}
        pageSizeOptions={[1000, 2000, 3000, 5000]}
        pagination
        checkboxSelection
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={rowSelectionModel}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        rowHeight={40}
        density="compact"
        detailPanelExpandedRowIds={expandedRows}
        onDetailPanelExpandedRowIdsChange={(newExpandedRows) => {
          setExpandedRows(new Set(newExpandedRows));
        }}
        // Enable built-in filtering
        filterMode="client"
        // Enable column menu to allow DataGrid Pro filtering
        disableColumnMenu={false}
        // Enable built-in column reordering
        disableColumnReorder={false}
        // Enable built-in column resizing
        disableColumnResize={false}
        // Enable built-in column pinning
        disableColumnPinning={false}
        // Enable built-in column visibility toggle
        disableColumnSelector={false}
        // Enable built-in density selector
        disableDensitySelector={false}
        // Enable built-in export
        disableExportSelector={false}
        // Enable built-in toolbar (this should show the column management button)
        showToolbar={true}
        // Event handlers for state tracking
        onFilterModelChange={handleFilterModelChange}
        onColumnVisibilityModelChange={(model) => {
          if (onColumnStateChange) {
            const hasHidden = Object.values(model).some(visible => !visible);
            onColumnStateChange(hasHidden);
          }
        }}
        sx={{
          ".MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.mode == 'dark' && "#121212",
          },
          ".MuiDataGrid-virtualScroller": {
            backgroundColor: theme.palette.mode == 'dark' && "#121212",
          },
          ".MuiDataGrid-row": {
            backgroundColor: theme.palette.mode == 'dark' && "#121212",
            ":hover": {
              backgroundColor: theme.palette.mode == 'dark' && "#202020"
            }
          },
          
          height: "calc(100vh - 30px)",
          overflow: "scroll",
          '& .MuiDataGrid-row': {
            minHeight: '40px !important',
            maxHeight: '40px !important',
            '&.highlight': {
              backgroundColor: `${theme.palette.warning.light} !important`,
              '& .MuiDataGrid-cell': {
                backgroundColor: `${theme.palette.warning.light} !important`,
              },
            },
          },
          '& .MuiDataGrid-cell': {
            padding: '4px 8px',
            fontSize: '0.875rem',
          },
          '& .MuiDataGrid-columnHeaders': {
            minHeight: '48px !important',
            maxHeight: '48px !important',
            backgroundColor: theme.palette.grey[100],
          },
          '& .MuiDataGrid-columnHeader': {
            padding: '4px 8px',
            fontSize: '0.875rem',
          },
          // Fix detail panel z-index (do not use order - it causes panels to appear at bottom)
          '& .MuiDataGrid-detailPanel': {
            zIndex: 1,
            marginTop: '0 !important',
            marginBottom: '0 !important',
          },
           '& .MuiDataGrid-detailPanelToggleCell': {
             zIndex: 2,
             cursor: 'pointer !important',
             '&:hover': {
               backgroundColor: theme.palette.action.hover,
             },
             // Ensure the toggle button is clickable
             '& .MuiDataGrid-detailPanelToggle': {
               cursor: 'pointer !important',
               pointerEvents: 'auto !important',
             },
           },
           '& .MuiDataGrid-row': {
             zIndex: 1,
           },
          // Ensure proper spacing between rows
          '& .MuiDataGrid-virtualScroller': {
            '& .MuiDataGrid-row': {
              '&:not(:last-child)': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
            },
          },
          // Fix detail panel content positioning
          '& .MuiDataGrid-detailPanelContent': {
            padding: '16px !important',
            margin: '0 !important',
          },
          // Ensure toggle icons are visible and clickable
          '& .MuiDataGrid-detailPanelToggle': {
            color: theme.palette.primary.main,
            fontSize: '1.2rem',
            '&:hover': {
              color: theme.palette.primary.dark, 
            },
          },
          // Fix any potential pointer event issues
          '& .MuiDataGrid-cell': {
            '&.MuiDataGrid-detailPanelToggleCell': {
              pointerEvents: 'auto !important',
            },
          },
        }}
      />
    </Box>
  );
});

export default TradeBlotterTable; 